import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { StepInitStatus } from 'components/layout';
import { useLocationParams } from 'hooks';
import {
  getPartnerships,
  queryKeys as cabinetQueryKeys,
} from 'features/cabinet/facebook';
import { Platform } from '../../../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import {
  getAdSettings,
  type AdSettingsRequestAdSet,
  type AdSettingsResponse,
} from '../api/getAdSettings';
import { getCallToActions } from '../api/getCallToActions';
import { getConversionDomain } from '../api/getConversionDomain';
import queryKeys from '../api/queryKeys';
import { createDefaultPartnershipAdSetting } from '../utils/partnershipAdSettings';
import {
  collectMissingAdSettingIds,
  isDeepLinkStepPass,
} from '../utils/deepLinkInit';
import {
  alignAdSettingsSlots,
  hasAdSettingsEntries,
  normalizeAdSettingsByIndex,
} from '../validation/adSettingsValidation';
import type {
  DeepLinkDependentStepGate,
  DeepLinkStepResult,
} from './deepLinkTypes';

export interface UseDeepLinkAdStepParams extends DeepLinkDependentStepGate {
  restoredFlows: string[];
  restoredAdSetsForPartnershipSettings: AdSettingsRequestAdSet[];
}

const resolveAdStepStatus = ({
  enabled,
  previousPass,
  isLoading,
  isError,
  hasMissing,
}: {
  enabled: boolean;
  previousPass: boolean;
  isLoading: boolean;
  isError: boolean;
  hasMissing: boolean;
}): StepInitStatus => {
  if (!enabled || !previousPass) return 'idle';
  if (isLoading) return 'loading';
  if (isError || hasMissing) return 'failed';
  return 'success';
};

const useDeepLinkAdStep = ({
  enabled,
  previousPass,
  restoredFlows,
  restoredAdSetsForPartnershipSettings,
}: UseDeepLinkAdStepParams): DeepLinkStepResult => {
  const { setValue } = useFormContext();
  const formApp = useWatch({ name: 'app' }) as string | undefined;
  const {
    app: appParam,
    platform: platformParam,
    callToAction: callToActionParam,
    conversionDomain: conversionDomainParam,
    hotjar: hotjarParam,
    partnershipAdsEnabled: partnershipAdsEnabledParam,
    partnershipIdentityToShow: partnershipIdentityToShowParam,
    partnershipIsPartnerIdentityFirst: partnershipIsPartnerIdentityFirstParam,
    partnershipCreationMode: partnershipCreationModeParam,
    partners: partnersParam,
    packAdSettings: packAdSettingsParam,
    partnershipAdSettingsFields: partnershipAdSettingsFieldsParam,
  } = useLocationParams();

  const resolvedApp = appParam ?? formApp;
  const isWebPlatform = String(platformParam) === String(Platform.WEB);
  const needsCtaLookup = enabled && !!callToActionParam;
  const needsDomainLookup = enabled && isWebPlatform && !!conversionDomainParam;
  const needsPartnershipIdCheck =
    enabled && partnershipAdsEnabledParam && partnersParam.length > 0;
  const needsPackAdSettingsCheck =
    enabled &&
    !partnershipAdsEnabledParam &&
    hasAdSettingsEntries(packAdSettingsParam);
  const needsPartnerAdSettingsCheck =
    enabled &&
    partnershipAdsEnabledParam &&
    partnersParam.length > 0 &&
    hasAdSettingsEntries(partnershipAdSettingsFieldsParam);

  const ctaQuery = useQuery({
    queryKey: [...queryKeys.callToActions, platformParam ?? ''],
    queryFn: ({ signal }) =>
      getCallToActions({ platform: platformParam ?? undefined }, signal),
    enabled: needsCtaLookup && previousPass && !!platformParam,
    staleTime: 60 * 30 * 1000,
  });

  const domainQuery = useQuery({
    queryKey: [...queryKeys.domains, resolvedApp ?? ''],
    queryFn: ({ signal }) =>
      getConversionDomain({ app: resolvedApp ?? undefined }, signal),
    enabled: needsDomainLookup && previousPass && !!resolvedApp,
    staleTime: 60 * 30 * 1000,
  });

  const partnershipsQuery = useQuery({
    queryKey: [cabinetQueryKeys.partnerships, { limit: 1000, offset: 1 }],
    queryFn: ({ signal }) =>
      getPartnerships({ limit: 1000, offset: 1 }, signal),
    enabled:
      (needsPartnershipIdCheck || needsPartnerAdSettingsCheck) && previousPass,
    staleTime: 60 * 30 * 1000,
  });

  const packAdSettingsRequestBody = useMemo(
    () => ({
      app: resolvedApp ?? '',
      platform: platformParam ?? '',
      flows: restoredFlows,
    }),
    [platformParam, resolvedApp, restoredFlows]
  );

  const packAdSettingsQuery = useQuery({
    queryKey: [
      ...queryKeys.adSettings,
      Object.values(packAdSettingsRequestBody).join(','),
    ],
    queryFn: () =>
      getAdSettings(
        packAdSettingsRequestBody,
        `${queryKeys.adSettings.join('/')}/`
      ),
    enabled:
      needsPackAdSettingsCheck &&
      previousPass &&
      restoredFlows.length > 0 &&
      Boolean(resolvedApp) &&
      Boolean(platformParam),
    staleTime: 0,
  });

  const partnerAdSettingsQueries = useQueries({
    queries: partnersParam.map((partnerId) => {
      const partnership = partnershipsQuery.data?.data.find(
        (entry) => String(entry.id) === partnerId
      );
      const appPageId = (partnership?.app_page_id ?? '').trim();
      const requestBody = {
        app: resolvedApp ?? '',
        platform: platformParam ?? '',
        ad_sets: restoredAdSetsForPartnershipSettings,
        app_page_id: appPageId,
      };

      return {
        queryKey: [
          ...queryKeys.partnershipAdSettings,
          Object.values(requestBody).join(','),
        ],
        queryFn: () =>
          getAdSettings(
            requestBody,
            `${queryKeys.partnershipAdSettings.join('/')}/`
          ),
        enabled:
          needsPartnerAdSettingsCheck &&
          previousPass &&
          Boolean(partnershipsQuery.data) &&
          Boolean(resolvedApp) &&
          Boolean(platformParam) &&
          Boolean(appPageId),
        staleTime: 0,
      };
    }),
  });

  const ctaMissing = useMemo(() => {
    if (!callToActionParam || !ctaQuery.data) return [];
    return ctaQuery.data.some((entry) => entry.code === callToActionParam)
      ? []
      : [callToActionParam];
  }, [callToActionParam, ctaQuery.data]);

  const domainMissing = useMemo(() => {
    if (!conversionDomainParam || !domainQuery.data) return [];
    return domainQuery.data.some(
      (entry) => entry.domain === conversionDomainParam
    )
      ? []
      : [conversionDomainParam];
  }, [conversionDomainParam, domainQuery.data]);

  const partnersMissing = useMemo(() => {
    const partnerships = partnershipsQuery.data;
    if (!partnerships) return [];
    return partnersParam.filter(
      (id) => !partnerships.data.some((entry) => String(entry.id) === id)
    );
  }, [partnersParam, partnershipsQuery.data]);

  const packAdSettingsMissing = useMemo(() => {
    if (!needsPackAdSettingsCheck) return [];
    if (restoredFlows.length === 0) {
      return packAdSettingsParam.flatMap((slot) => Object.keys(slot ?? {}));
    }
    if (!packAdSettingsQuery.data) return [];
    const data = packAdSettingsQuery.data;
    return collectMissingAdSettingIds(
      packAdSettingsParam,
      (_index, flowName) => data[flowName]
    );
  }, [
    needsPackAdSettingsCheck,
    packAdSettingsParam,
    packAdSettingsQuery.data,
    restoredFlows.length,
  ]);

  const partnerAdSettingsMissing = useMemo(() => {
    if (!needsPartnerAdSettingsCheck || !partnershipsQuery.data) return [];

    const missingAppPages = partnersParam.filter((partnerId) => {
      const partnership = partnershipsQuery.data!.data.find(
        (entry) => String(entry.id) === partnerId
      );
      return partnership != null && !(partnership.app_page_id ?? '').trim();
    });

    const readyResponses = partnerAdSettingsQueries.every((query, index) => {
      const partnerId = partnersParam[index];
      if (
        partnersMissing.includes(partnerId) ||
        missingAppPages.includes(partnerId)
      ) {
        return true;
      }
      return Boolean(query.data);
    });
    if (!readyResponses) return missingAppPages;

    const settingsMissing = collectMissingAdSettingIds(
      partnershipAdSettingsFieldsParam,
      (index, flowName) => {
        const data = partnerAdSettingsQueries[index]?.data as
          | AdSettingsResponse
          | undefined;
        return data?.[flowName];
      }
    );

    return [...missingAppPages, ...settingsMissing];
  }, [
    needsPartnerAdSettingsCheck,
    partnerAdSettingsQueries,
    partnersMissing,
    partnersParam,
    partnershipAdSettingsFieldsParam,
    partnershipsQuery.data,
  ]);

  const missing = useMemo(
    () => [
      ...ctaMissing,
      ...domainMissing,
      ...partnersMissing,
      ...packAdSettingsMissing,
      ...partnerAdSettingsMissing,
    ],
    [
      ctaMissing,
      domainMissing,
      packAdSettingsMissing,
      partnerAdSettingsMissing,
      partnersMissing,
    ]
  );

  const isLoading =
    (needsCtaLookup && ctaQuery.isPending) ||
    (needsDomainLookup && domainQuery.isPending) ||
    ((needsPartnershipIdCheck || needsPartnerAdSettingsCheck) &&
      partnershipsQuery.isPending) ||
    (needsPackAdSettingsCheck &&
      restoredFlows.length > 0 &&
      packAdSettingsQuery.isPending) ||
    (needsPartnerAdSettingsCheck &&
      partnerAdSettingsQueries.some((query) => query.isPending));

  const isError =
    ctaQuery.isError ||
    domainQuery.isError ||
    ((needsPartnershipIdCheck || needsPartnerAdSettingsCheck) &&
      partnershipsQuery.isError) ||
    (needsPackAdSettingsCheck && packAdSettingsQuery.isError) ||
    (needsPartnerAdSettingsCheck &&
      partnerAdSettingsQueries.some((query) => query.isError));

  const status = resolveAdStepStatus({
    enabled,
    previousPass,
    isLoading,
    isError,
    hasMissing: missing.length > 0,
  });

  const restore = useCallback(() => {
    if (!enabled) return;

    if (callToActionParam) {
      setValue('callToActions', callToActionParam);
    }
    if (conversionDomainParam) {
      setValue('selectedConversionDomain', conversionDomainParam);
    }
    setValue('hotjar', hotjarParam);
    setValue('partnershipAdsEnabled', partnershipAdsEnabledParam);

    if (partnershipAdsEnabledParam) {
      if (partnersParam.length) {
        setValue(
          'partnershipAdSettings',
          partnersParam.map((partnershipId) => ({
            ...createDefaultPartnershipAdSetting(),
            partnershipId,
          }))
        );
      }
      setValue(
        'partnershipAdSettingsFields',
        alignAdSettingsSlots(
          partnershipAdSettingsFieldsParam,
          Math.max(partnersParam.length, 1)
        )
      );
      setValue(
        'partnershipIdentityToShow',
        partnershipIdentityToShowParam ?? PartnershipIdentityToShow.BOTH
      );
      setValue(
        'partnershipIsPartnerIdentityFirst',
        partnershipIsPartnerIdentityFirstParam ?? true
      );
      setValue(
        'partnershipCreationMode',
        partnershipCreationModeParam ?? PartnershipCreationMode.PARTNERSHIP_ONLY
      );
      return;
    }

    if (hasAdSettingsEntries(packAdSettingsParam)) {
      setValue(
        'packAdSettings',
        normalizeAdSettingsByIndex(packAdSettingsParam)
      );
    }
  }, [
    callToActionParam,
    conversionDomainParam,
    enabled,
    hotjarParam,
    packAdSettingsParam,
    partnersParam,
    partnershipAdsEnabledParam,
    partnershipAdSettingsFieldsParam,
    partnershipCreationModeParam,
    partnershipIdentityToShowParam,
    partnershipIsPartnerIdentityFirstParam,
    setValue,
  ]);

  return {
    status,
    pass: isDeepLinkStepPass(enabled, status),
    missing,
    restore,
  };
};

export default useDeepLinkAdStep;
